
/**
 * @author Jasper Howard <jhoward@peppermillcas.com>
 * @version 0.1.0
 * @copyright  Copyright (C) 2025-2026, Peppermill Inc. All rights reserved. Redistribution and use in source and binary forms, with or without modification, are not permitted.
 * @changelog
 *  2025-04-09 (1.0.0) (jhoward)
 *    - <section-item-subgroup> handles displaying a subgroup of dining menu items.
 *
 *  2025-04-09 (1.0.1) (jhoward)
 *    - Add <EasterFoodItem> to <section-item-subgroup>.
 *
 *  2025-05-05 (1.0.2) (jhoward)
 *    - Add <MothersDayFoodItem> to <section-item-subgroup>.
 *
 * 2026-02-17 (0.1.0) (jhoward)
 *   - Import and register App\Library\Js\Components\Menu\Item\Chi_chineseNewYearItem.vue as ChiChineseNewYearItem.
 *   - Enables rendering of Chinese New Year menu items within section subgroups.
 */

import {defineComponent, PropType} from 'vue'
import {
  methodGetPricingTypes,
  methodShowBIN
} from "../../../helpers/menu-sections";
import {ALLOWED_ITEM_COMPONENTS, MENU_ITEM_GROUP_LAYOUT, MENU_ITEM_TAG_LOCATIONS, MenuItem, MenuSection} from "../defs";
import BiminiFoodItem from "../item/Bimini_foodItem.vue";
import BiminiSteakItem from "../item/Bimini_steakItem.vue";
import ChiDefaultItem from "../item/Chi_defaultItem.vue";
import ChiChineseNewYearItem from "../item/Chi_chineseNewYearItem.vue";
import ChiWineListItem from "../item/Chi_wineListItem.vue";
import MenuItemTextOnly from "../item/MenuItemTextOnly.vue";
import MenuItemHighlightedBox from "../item/MenuItemHighlightedBox.vue";
import BiminiCocktailItem from "../item/Bimini_cocktailItem.vue";
import HighLimitFoodItem from "../item/HighLimit_foodItem.vue";
import oceanoDefaultItem from "../item/OceanoDefaultItem.vue";
import PanchoWilliesFoodItem from "../item/PanchoWillies_foodItem.vue";
import PanchoWilliesDrinkItem from "../item/PanchoWillies_drinkItem.vue";
import PanchoWilliesCervezasItem from "../item/PanchoWillies_cervezasItem.vue";
import WvSteakHouseFoodItem from "../item/WvSteakHouseFoodItem.vue";
import WvSteakHouseSteakItem from "../item/WvSteakHouseSteakItem.vue";
import WvSteakHouseCocktailItem from "../item/WvSteakHouseCocktailItem.vue";
import WvSteakHouseSignatureDrinkItem from "../item/WvSteakHouseSignatureDrinkItem.vue";
import WvSteakHouseWineListItem from "../item/WvSteakHouseWineListItem.vue";
import BackBarDefaultItem from "../item/BackBarDefaultItem.vue";
import ThanksgivingFoodItem from "../item/Thanksgiving_foodItem.vue";
import NewYearsFoodItem from "../item/NewYears_foodItem.vue";
import ChristmasFoodItem from "../item/Christmas_foodItem.vue";
import ChristmasDrinkItem from "../item/Christmas_drinkItem.vue";
import ValentinesDayFoodItem from "../item/ValentinesDay_foodItem.vue";
import EasterFoodItem from "../item/Easter_foodItem.vue";
import MothersDayFoodItem from "../item/MothersDay_foodItem.vue";
import MothersDayCocktailItem from "../item/MothersDay_cocktailItem.vue";
import DefaultItem from "../item/defaultItem.vue";
import WineListItem from "../item/wineListItem.vue";
import MenuItemPrice from "../item/MenuItemPrice.vue";
import RomanzaDefaultItem from "../item/Romanza_defaultItem.vue";
import RomanzaDessertItem from "../item/Romanza_dessertItem.vue";

export default defineComponent({
  name: "SectionItemSubgroup",
  components: {
    MenuItemPrice,
    BiminiFoodItem,
    BiminiSteakItem,
    BiminiCocktailItem,
    ChiDefaultItem,
    ChiChineseNewYearItem,
    ChiWineListItem,
    MenuItemTextOnly,
    MenuItemHighlightedBox,
    HighLimitFoodItem,
    oceanoDefaultItem,
    PanchoWilliesFoodItem,
    PanchoWilliesDrinkItem,
    PanchoWilliesCervezasItem,
    WvSteakHouseFoodItem,
    WvSteakHouseSteakItem,
    WvSteakHouseCocktailItem,
    WvSteakHouseSignatureDrinkItem,
    WvSteakHouseWineListItem,
    BackBarDefaultItem,
    ThanksgivingFoodItem,
    NewYearsFoodItem,
    ChristmasFoodItem,
    ChristmasDrinkItem,
    ValentinesDayFoodItem,
    EasterFoodItem,
    MothersDayFoodItem, MothersDayCocktailItem,
    DefaultItem,
    WineListItem,
    RomanzaDefaultItem, RomanzaDessertItem,
  },
  props: {
    items: {
      type: Array as PropType<MenuItem[]>,
      required: true,
    },
    groupTitle: {
      type: String,
      required: true,
    },
    subgroupTitle: {
      type: String,
      required: true,
    },
    menuItemComponent: {
      type: String,
      required: true,
      validate: (value: string) => ALLOWED_ITEM_COMPONENTS.indexOf(value)
    },
    menuItemTagLocation: {
      type: String as PropType<MENU_ITEM_TAG_LOCATIONS>,
      default: ''
    },
    section: {
      type: Object as PropType<MenuSection>,
      required: true
    },
    layout: {
      type: String as PropType<MENU_ITEM_GROUP_LAYOUT>,
      default: ''
    },
    isSubgroupPricingShown: {
      type: Boolean,
      default: false
    },
    pricingTypes: {
      type: Array as PropType<string[]>,
      default: () => []
    },
    isPageLinkGrid: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      ALLOWED_ITEM_COMPONENTS
    };
  },
  computed: {
    isBinShowing(): boolean {
      return this.showBIN(this.items, this.section);
    },
    subgroupClasses(): { [key: string]: boolean } {
      return {
        'menu--item_subgroup-show_BIN': this.isBinShowing,
        'menu--item_subgroup-show-pricing': this.isSubgroupPricingShown
      };
    },
  },
  methods: {
    getPricingTypes: methodGetPricingTypes,
    showBIN: methodShowBIN,

    getMenuItemComponentOverride(item: MenuItem, defaultValue?: string): string {
      if (item.mi_custom_flipbook_component.length) {
        return item.mi_custom_flipbook_component;
      } else if ('string' === typeof defaultValue && -1 !== this.ALLOWED_ITEM_COMPONENTS.indexOf(defaultValue)) {
        return defaultValue;
      }

      return this.menuItemComponent;
    },

    handleItemRows(rows: number) {
      this.$emit('rows', rows);
    },

    sectionContentClasses(section: MenuSection): { [key: string]: boolean } {
      const output: { [key: string]: boolean } = {};
      if (this.items) {
        output['item-count-' + this.items.length] = true;
      }
      return output;
    },
  },
})
